import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownItem, InlineAlert, Text } from '@amzn/storm-ui';
import FilterDropdown from '@amzn/storm-ui-filter-dropdown';
import { StatusTypes } from '../../constants/statusTypes';
import { StyledText } from './FilterDropdownWrapper.styles';
import { FilterDropdownWrapperProps, Option } from './interface/interfaces';
import { FUSE_OPTIONS } from './config/config';

export const FilterDropdownWrapper = ({
  componentId,
  id,
  placeholder,
  value,
  onChange,
  disabled,
  error,
  dropdownOptions,
  persistentBottomOption,
}: FilterDropdownWrapperProps) => {
  const { t } = useTranslation();
  const [filteredItems, setFilteredItems] = useState<Option[]>([
    ...dropdownOptions,
  ]);
  const [isFilterActive, setIsFilterActive] = useState(false);

  const optionsKey = JSON.stringify(dropdownOptions);

  // Keep filteredItems in sync when dropdownOptions change (e.g. async data load)
  useEffect(() => {
    setFilteredItems([...dropdownOptions]);
    setIsFilterActive(false);
  }, [optionsKey]);

  const handleFilterChange = (results: Option[]) => {
    setFilteredItems(results);
    setIsFilterActive(true);
  };

  const handleFilterClear = () => {
    setFilteredItems([...dropdownOptions]);
    setIsFilterActive(false);
  };

  const handleDropdownChange = (val: string) => onChange?.(val);

  const showPersistentBottomOption =
    persistentBottomOption && (!isFilterActive || filteredItems.length === 0);

  const overrideLabel = useCallback(
    (_: string, val: string) => {
      if (val === '') return t(placeholder || 'select-option');

      if (!!persistentBottomOption && val === persistentBottomOption.value)
        return persistentBottomOption.label;

      const matchedOption = dropdownOptions.find(
        (opt) => opt.value.toString() === val.toString()
      );
      return matchedOption ? matchedOption.label : val;
    },
    [placeholder, dropdownOptions, persistentBottomOption]
  );

  const NoResultsFoundComponent = useMemo(() => {
    if (filteredItems.length === 0 && !persistentBottomOption) {
      return () => (
        <StyledText>
          <Text>{t('no-results-found')}</Text>
        </StyledText>
      );
    }
    return undefined;
  }, [filteredItems.length, persistentBottomOption]);

  return (
    <>
      <FilterDropdown
        id={componentId}
        searchInputId={`${id}-filter-search`}
        placeholder={t(placeholder || 'select-option')}
        onChange={handleDropdownChange}
        onFilterChange={handleFilterChange}
        onFilterClear={handleFilterClear}
        selectedValue={value?.toString() || ''}
        onOverrideLabel={overrideLabel}
        items={dropdownOptions}
        options={FUSE_OPTIONS}
        disabled={disabled}
        fullWidth
        mobileFullScreen
        postRender={NoResultsFoundComponent}
        {...(error && { statusType: StatusTypes.ERROR })}
      >
        <DropdownItem id={`${id}-placeholder`} value="">
          {t(placeholder || 'select-option')}
        </DropdownItem>
        {filteredItems.map((option) => (
          <DropdownItem key={option.value} id={option.id} value={option.value}>
            {option.label}
          </DropdownItem>
        ))}
        {showPersistentBottomOption && (
          <DropdownItem
            key={persistentBottomOption.value}
            id={persistentBottomOption.id}
            value={persistentBottomOption.value}
          >
            {persistentBottomOption.label}
          </DropdownItem>
        )}
      </FilterDropdown>
      {error && <InlineAlert message={error} variant="error" />}
    </>
  );
};
